import { Table, Column, FieldType, useUIState, Section, useEffectAfterMount, FieldValue, Async, Icon, Tooltip } from "../../../../features/ui"
import { TableName } from "../../../../tables"
import { useEffect, useMemo, useState } from "react"
import { Item } from "../../state/types"
import { ProductName, useLotLoaders, useProductHooks, useProductLoaders } from "../../../products"
import { Data, Data2, useDataTypesHooks, INITIAL_SORT_STATE, SortState, nextSortState, sortRows, withSortHeaders } from "../../../../features/data-types"
import { usePricesHooks } from "../../hooks/pricesHooks"
import { usePriceOutlineHooks } from "../../hooks/priceOutlineHooks"
import { useFind } from "../../../../state/reducers/hooks"
import styles from './operationItemsTable.module.css'

type Props = {
    itemsData: Partial<Item>[]
    createMode?: boolean
    customerTypeId?: string
    priceLabel?: string
    discountLabel?: string
    disableCreate?: boolean
    readOnly?: boolean
    onCreateItem?: () => void
    onEditItem?: (productId: string, lotId?: string) => void
    onRemoveItem?: (productId: string, lotId?: string) => void
    renderDetail?: (row: Data2) => JSX.Element
}

export const OperationItemsTable = (props: Props) => {
    const { itemsData, createMode = true, customerTypeId, priceLabel, discountLabel, disableCreate, readOnly, onCreateItem, onEditItem, onRemoveItem, renderDetail } = props

    const { openedModal } = useUIState()

    const [rows, setRows] = useState<Data2[]>([])
    const [focusAddItem, setFocusAddItem] = useState(false)
    const [sortState, setSortState] = useState<SortState>(INITIAL_SORT_STATE)

    const { describeQuantity } = useProductHooks()
    const { getItemPriceOutline, getItemPriceOutlineLive } = usePriceOutlineHooks()
    const { formatPercentage, formatPrice, formatNumber } = useDataTypesHooks()
    const pricesHooks = usePricesHooks()
    const find = useFind()

    const productLoaders = useProductLoaders()
    const lotLoaders = useLotLoaders()

    useEffectAfterMount(() => {
        setFocusAddItem(!openedModal)
    }, [openedModal])

    const loadRows = async () => {
        const { loadProduct } = productLoaders()
        return Promise.all(itemsData.map(async data => {
            const product = await loadProduct(data.productId!)
            const { customerUnitSubtotal, specialDiscountUnitSubtotal } = createMode ?
                getItemPriceOutlineLive(data, customerTypeId) :
                getItemPriceOutline(data as Item)
            const discountYield = pricesHooks.adjustment.calculateYield(specialDiscountUnitSubtotal, customerUnitSubtotal)
            return {
                productLotId: data.lotId ? [data.productId!, data.lotId].join(':') : data.productId!,
                price: customerUnitSubtotal,
                discounts: data.discounts,
                specialDiscountYield: data.specialDiscountYield,
                discountedPrice: discountYield ? `(${formatPercentage(discountYield)}) ${formatPrice(specialDiscountUnitSubtotal)}` : undefined,
                quantity: describeQuantity(data.quantity!, product!),
                cancelledQuantity: data.cancelledQuantity,
                specialDiscountUnitSubtotal,
                totalPrice: specialDiscountUnitSubtotal * data.quantity!
            }
        }))
    }

    useEffect(() => {
        loadRows().then(setRows)
    }, [itemsData, customerTypeId])

    const title = useMemo(() => rows.length === 0 ? 'Productos' : undefined, [rows])

    const onColumnSort = (name: string) => setSortState(prev => nextSortState(prev, name))

    const columns: Column[] = [
        {
            name: 'productLotId',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Producto',
            sortValue: (value?: FieldValue) => {
                const productId = (value as string).split(':')[0]
                return (find(TableName.PRODUCTS, productId) as { name?: string })?.name ?? ''
            },
            render: (value?: FieldValue) => <Async
                loadFn={async () => {
                    const [productId, lotId] = (value! as string).split(':')
                    const data = itemsData.find(data => data.productId === productId && data.lotId === lotId)
                    const product = data?.productId ? await productLoaders().loadProduct(data.productId) : undefined
                    const lot = data?.lotId ? await lotLoaders().loadLot(data.lotId) : undefined
                    return <ProductName product={product} lot={lot} showSupplierCode />
                }}
                deps={[itemsData]}
            />
        },
        {
            name: 'price',
            type: FieldType.PRICE,
            label: priceLabel || 'Precio de Lista'
        },
        {
            name: 'quantity',
            type: FieldType.TEXT,
            label: 'Cantidad',
            sortable: false
        },
        {
            name: 'totalPrice',
            type: FieldType.PRICE,
            label: 'Precio Total'
        }
    ]
    if (itemsData.some(item => item.cancelledQuantity)) {
        const cancelledQuantityColumn = {
            name: 'cancelledQuantity',
            type: FieldType.TEXT,
            label: '',
            render: (value?: FieldValue) => {
                const cancelledQuantity = value! as number
                return (
                    <div className={styles.cancelledQuantity}>
                        {formatNumber(cancelledQuantity)}
                        <Icon icon="slash-circle" color="tertiary" small />
                        <Tooltip tooltip={`${formatNumber(cancelledQuantity)} cancelado${cancelledQuantity > 1 ? 's' : ''}`} className={styles.tooltip} />
                    </div>
                )
            }
        }
        columns.splice(3, 0, cancelledQuantityColumn)
    }
    if (itemsData.some(item => item.specialDiscountYield || (item.discounts || []).length > 0)) {
        const discountedPriceColumn = {
            name: 'discountedPrice',
            type: FieldType.TEXT,
            label: discountLabel || 'Descuentos',
            sortable: false
        }
        columns.splice(2, 0, discountedPriceColumn)
    }

    const sortedRows = useMemo(() => {
        if (!sortState.column || !sortState.direction) return rows
        const col = columns.find(c => c.name === sortState.column)
        if (!col) return rows
        return sortRows(rows as Data[], col, sortState.direction)
    }, [rows, sortState])

    const sortableColumns = withSortHeaders(columns, sortState, onColumnSort)

    const handleEditItem = (productLotId: string) => {
        const [productId, lotId] = productLotId.split(':')
        onEditItem && onEditItem(productId, lotId)
    }

    const handleRemoveItem = (productLotId: string) => {
        const [productId, lotId] = productLotId.split(':')
        onRemoveItem && onRemoveItem(productId, lotId)
    }

    return (
        <Section
            id='operationItems'
            title={title}
            space={12}
        >
            <Table
                columns={sortableColumns}
                rows={sortedRows as Data[]}
                rowId='productLotId'
                readOnly={readOnly}
                createIcon="plus"
                disableCreate={disableCreate}
                focusCreate={focusAddItem}
                small
                onCreate={onCreateItem}
                onEdit={handleEditItem}
                onRemove={handleRemoveItem}
                renderDetail={renderDetail}
            />
        </Section>
    )
}
